import { useMemo } from 'react';
import usePurchase from './store/usePurchase';
import { useWhitelabelFeatures } from './whitelabel/useWhitelabelFeatures';
import useWhitelabelEnvs from './whitelabel/useWhitelabelEnvs';

/**
 * Provider configuration
 * Created per client request to homologate forms within Reservamos and Smilebus.
 * The PHONE_NUMBER_PER_PASSENGER flag shows the phone field for ALL providers when enabled.
 * This provider-specific configuration allows selective display based on transporterName,
 * showing the phone field ONLY for configured providers (e.g., Smilebus) without affecting other tenants.
 *
 * The list of providers is configured via the 'providersWithPhoneNumber' environment variable from the API.
 */

/**
 * Check if a provider requires phone number field to be shown
 * @param {string} transporterName - The transporter name to check
 * @param {Array} configuredProviders - Array of provider names from API configuration
 * @returns {boolean} True if the provider requires phone number field
 */
const isProviderConfigured = (transporterName, configuredProviders) => {
  return transporterName && configuredProviders?.includes(transporterName);
};

/**
 * Extracts transporter name from trip fragments
 * @param {Object} trip - The trip object
 * @returns {string|undefined} The transporter name
 */
const getTransporterName = (trip) => trip?.fragments?.[0]?.line?.transporterName;

/**
 * Hook to determine if phone number field should be displayed based on provider
 *
 * This hook extracts transporter names from the purchase state and determines
 * if the phone number field should be displayed based on configured providers.
 * Uses useMemo to avoid recalculating on every render.
 *
 * @returns {boolean} True if phone number field should be shown
 */
function useProviderPhoneDisplay() {
  const purchase = usePurchase();
  const features = useWhitelabelFeatures();
  const env = useWhitelabelEnvs();

  const shouldShowPhoneField = useMemo(() => {
    const configuredProviders = env?.providersWithPhoneNumber || [];
    const departureTransporterName = getTransporterName(purchase?.departs);
    const returnTransporterName = getTransporterName(purchase?.returns);
    const isGlobalFeatureEnabled = features.PHONE_NUMBER_PER_PASSENGER;

    const isDepartureConfigured = isProviderConfigured(
      departureTransporterName,
      configuredProviders,
    );
    const isReturnConfigured = isProviderConfigured(returnTransporterName, configuredProviders);

    // Returns true if: global feature flag is enabled OR either trip uses a configured provider
    return isGlobalFeatureEnabled || isDepartureConfigured || isReturnConfigured;
  }, [
    purchase?.departs,
    purchase?.returns,
    features.PHONE_NUMBER_PER_PASSENGER,
    env?.providersWithPhoneNumber,
  ]);

  return shouldShowPhoneField;
}

export default useProviderPhoneDisplay;
